import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ViewChild,
    ViewEncapsulation,
    input,
    output,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { IngestionPoint } from "./data.ingestion.service";
import { S25IngestionPointDropdownComponent } from "../s25-dropdown/single-select/s25.ingestion.point.dropdown.component";

@TypeManagerDecorator("s25-ng-ingestion-search-form-selector")
@Component({
    selector: "s25-ng-ingestion-search-form-selector",
    template: `
        <div class="form-selector">
            <div class="search-all-option">
                <label class="checkbox-label">
                    <input type="checkbox" [ngModel]="searchAllForms()" (ngModelChange)="onSearchAllChanged($event)" />
                    Search across ALL forms
                </label>
                @if (searchAllForms()) {
                    <div class="warning">
                        <strong>Warning:</strong> Searching across all forms may take longer than selecting specific
                        forms.
                    </div>
                }
            </div>

            @if (!searchAllForms()) {
                <div class="form-selection">
                    <div class="form-dropdown">
                        <s25-ingestion-point-dropdown
                            #formDropdown
                            [enabledOnly]="false"
                            (valueChange)="onFormSelected($event)"
                        />
                    </div>
                </div>

                @if (selectedForms().length > 0) {
                    <div class="selected-forms">
                        <h5>Selected Forms ({{ selectedForms().length }}):</h5>
                        <div class="form-list">
                            @for (form of selectedForms(); track form.ingestionPointId) {
                                <div class="form-item">
                                    <span class="form-name">{{ form.ingestionPointName }}</span>
                                    <button class="btn btn-sm btn-danger" (click)="removeForm(form.ingestionPointId)">
                                        ×
                                    </button>
                                </div>
                            }
                        </div>
                    </div>
                }
            }
        </div>
    `,
    styles: `
        .form-selector {
            padding: 15px;
        }

        .search-all-option {
            margin-bottom: 20px;
        }

        .checkbox-label {
            display: flex;
            align-items: center;
            font-weight: 500;
            cursor: pointer;
        }

        .checkbox-label input {
            margin-right: 8px;
        }

        .warning {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 4px;
            padding: 10px;
            margin-top: 10px;
            color: #856404;
        }

        .form-selection {
            margin-bottom: 20px;
        }

        .form-dropdown {
            margin-bottom: 15px;
        }

        .selected-forms {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 15px;
        }

        .selected-forms h5 {
            margin: 0 0 10px 0;
            color: #495057;
        }

        .form-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        }

        .form-item {
            display: flex;
            align-items: center;
            background: white;
            border: 1px solid #ced4da;
            border-radius: 4px;
            padding: 5px 10px;
            font-size: 0.9em;
        }

        .form-name {
            margin-right: 8px;
        }

        .btn {
            padding: 2px 6px;
            border: none;
            background: transparent;
            cursor: pointer;
            border-radius: 3px;
            font-size: 0.8em;
        }

        .btn-sm {
            font-size: 0.875em;
        }

        .btn-danger {
            color: #dc3545;
        }

        .btn-danger:hover {
            background: #dc3545;
            color: white;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionSearchFormSelectorComponent {
    @ViewChild("formDropdown") formDropdown: S25IngestionPointDropdownComponent;

    selectedForms = input<IngestionPoint[]>([]);
    searchAllForms = input<boolean>(false);

    formsChanged = output<IngestionPoint[]>();
    searchAllChanged = output<boolean>();

    constructor(private cd: ChangeDetectorRef) {}

    onFormSelected(formData: any) {
        if (!formData || !formData.data) return;

        const form = formData.data as IngestionPoint;
        const currentForms = this.selectedForms();

        // Check if form is already selected
        if (currentForms.some((f) => f.ingestionPointId === form.ingestionPointId)) {
            return;
        }

        // Add form to selection
        const newForms = [...currentForms, form];
        this.formsChanged.emit(newForms);

        // Reset dropdown after change detection cycle
        setTimeout(() => {
            if (this.formDropdown) {
                this.formDropdown.value.set(undefined);
                this.cd.detectChanges();
            }
        }, 0);
    }

    removeForm(ingestionPointId: number) {
        const newForms = this.selectedForms().filter((f) => f.ingestionPointId !== ingestionPointId);
        this.formsChanged.emit(newForms);
    }

    onSearchAllChanged(value: boolean) {
        this.searchAllChanged.emit(value);
    }
}
